import type * as React from 'react'
import { useCallback } from 'react'

import { Button, CoachMark, Flex, NewBadge } from '@buffer-mono/popcorn'

import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'

const BANNER_ID = 'threads-location-product-discovery'

interface ThreadsLocationProductDiscoveryNotificationProps {
  visible: boolean
  children: React.ReactNode
}

export const ThreadsLocationProductDiscoveryNotification: React.FC<
  ThreadsLocationProductDiscoveryNotificationProps
> = ({ visible, children }) => {
  const { isActive, dismiss } = useDismissableBanner(BANNER_ID, {
    trackDismiss: true,
  })

  const handleLearnMore = useCallback(() => {
    window.open(
      'https://support.buffer.com/article/857-using-threads-with-buffer',
      '_blank',
    )
    dismiss()
  }, [dismiss])

  const shouldShowCoachMark = visible && isActive

  if (!shouldShowCoachMark) {
    return <>{children}</>
  }

  return (
    <CoachMark open={shouldShowCoachMark}>
      <CoachMark.Overlay style={{ display: 'block', width: '100%' }}>
        <CoachMark.Spotlight
          size="small"
          style={{
            '--spotlight-top': '50%',
            '--spotlight-left': '50%',
            zIndex: 1000,
          }}
        />
        {children}
      </CoachMark.Overlay>
      <CoachMark.Content
        side="bottom"
        align="start"
        showCloseIcon={false}
        style={
          {
            '--coach-mark-max-width': '340px',
          } as React.CSSProperties
        }
      >
        <Flex gap="xs" align="center">
          <NewBadge />
          <CoachMark.Title>Add a location to boost discovery</CoachMark.Title>
        </Flex>
        <CoachMark.Description>
          Add locations to your Threads posts to help more people discover your
          content.
        </CoachMark.Description>
        <CoachMark.Footer>
          <Button variant="tertiary" onClick={handleLearnMore}>
            Learn more
          </Button>
          <CoachMark.Dismiss>
            <Button variant="primary" onClick={dismiss}>
              Got it
            </Button>
          </CoachMark.Dismiss>
        </CoachMark.Footer>
      </CoachMark.Content>
    </CoachMark>
  )
}
